<script setup lang="ts">
import { asText } from '@prismicio/client'

const footer = useFooter()
const { client } = usePrismic()

const { data: services } = await useAsyncData('footer-services', () =>
  client.getAllByType('service', {
    orderings: [{ field: 'my.service.order' }],
  })
)

const { data: branches } = await useAsyncData('footer-branches', () =>
  client.getAllByType('branch', {
    orderings: [{ field: 'my.branch.order' }],
  })
)
</script>

<template>
  <footer class="pa-footer">
    <!-- Main footer content -->
    <div class="pa-footer__main">
      <div class="pa-container">
        <div class="pa-footer__grid">
          <!-- Brand & Contact -->
          <div class="pa-footer__brand">
            <div v-if="footer?.data.footer_logo?.url" class="pa-footer__brand-logo">
              <PrismicImage :field="footer.data.footer_logo" class="pa-footer__brand-logo-image" />
            </div>

            <div class="pa-footer__contact">
              <div v-if="footer?.data.address" class="pa-footer__contact-item">
                <svg class="pa-footer__contact-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                  <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 0 1 15 0Z" />
                </svg>
                <div>
                  <PrismicRichText :field="footer.data.address" />
                </div>
              </div>

              <div v-if="footer?.data.email" class="pa-footer__contact-item">
                <svg class="pa-footer__contact-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
                </svg>
                <span>E: {{ footer.data.email }}</span>
              </div>

              <div v-if="footer?.data.phone" class="pa-footer__contact-item">
                <svg class="pa-footer__contact-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z" />
                </svg>
                <span>T: {{ footer.data.phone }}</span>
              </div>
            </div>

            <div v-if="footer?.data.social_links?.length" class="pa-footer__social">
              <PrismicLink
                  v-for="(social, index) in footer.data.social_links"
                  :key="index"
                  :field="social.link"
                  class="pa-footer__social-link"
              >
                <svg v-if="social.platform === 'facebook'" class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                <svg v-else-if="social.platform === 'instagram'" class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                <svg v-else-if="social.platform === 'linkedin'" class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              </PrismicLink>
            </div>
          </div>

          <!-- Navigation Columns -->
          <div class="pa-footer__nav">
            <div class="pa-footer__nav-columns">
              <!-- Services (auto) -->
              <div v-if="services?.length">
                <div v-if="footer?.data.services_column_title" class="pa-footer__nav-title">
                  {{ footer.data.services_column_title }}
                </div>
                <div class="pa-footer__nav-list">
                  <NuxtLink
                      v-for="service in services"
                      :key="service.id"
                      :to="service.url || `/diensten/${service.uid}`"
                      class="pa-footer__nav-link"
                  >
                    {{ asText(service.data.title) }}
                  </NuxtLink>
                </div>
              </div>

              <!-- Branches (auto) -->
              <div v-if="branches?.length">
                <div v-if="footer?.data.branches_column_title" class="pa-footer__nav-title">
                  {{ footer.data.branches_column_title }}
                </div>
                <div class="pa-footer__nav-list">
                  <NuxtLink
                      v-for="branch in branches"
                      :key="branch.id"
                      :to="branch.url || `/sectoren/${branch.uid}`"
                      class="pa-footer__nav-link"
                  >
                    {{ asText(branch.data.title) }}
                  </NuxtLink>
                </div>
              </div>

              <!-- Custom column -->
              <div v-if="footer?.data.custom_column_title">
                <div class="pa-footer__nav-title">{{ footer.data.custom_column_title }}</div>
                <div class="pa-footer__nav-list">
                  <PrismicLink
                      v-for="(item, index) in footer.data.custom_column_links"
                      :key="index"
                      :field="item.link"
                      class="pa-footer__nav-link"
                  >
                    {{ item.label }}
                  </PrismicLink>
                </div>
              </div>
            </div>
          </div>
        </div>

        <hr v-if="footer?.data.partner_logos?.length" class="pa-footer__divider" />

        <!-- Partners -->
        <div v-if="footer?.data.partner_logos?.length" class="pa-footer__partners">
          <template v-for="(partner, index) in footer.data.partner_logos" :key="index">
            <PrismicLink v-if="partner.link && ('id' in partner.link || 'url' in partner.link)" :field="partner.link">
              <PrismicImage v-if="partner.logo?.url" :field="partner.logo" class="pa-footer__partner-logo" />
            </PrismicLink>
            <PrismicImage v-else-if="partner.logo?.url" :field="partner.logo" class="pa-footer__partner-logo" />
          </template>
        </div>
      </div>
    </div>

    <!-- Bottom bar -->
    <div class="pa-footer__bottom">
      <div class="pa-container">
        <div class="pa-footer__bottom-inner">
          <div class="pa-footer__bottom-links">
            <span v-if="footer?.data.copyright_text">{{ footer.data.copyright_text }}</span>
            <PrismicLink
                v-for="(item, index) in footer?.data.bottom_links"
                :key="index"
                :field="item.link"
                class="pa-footer__bottom-link"
            >
              {{ item.label }}
            </PrismicLink>
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>
